<template>
    <div class="customer-profile">
        <div class="sticky-top">
            <div
                :class="!$page.props.isProductionEnvironment ? 'non-production' : 'production'"
            >
                <DCard padding>
                    <div class="overview">
                        <main>
                            <div class="two-line-second-bold">
                                <span>{{ customer.brand.name }}</span>
                                <span>#{{ customer.id }}</span>
                            </div>
                            <div class="two-line">
                                <span>{{ customer.company }}</span>
                                <span>
                                    {{ customer.title ? __(`customers.common.titles.${customer.title.toLowerCase()}`) : '' }}
                                    {{ customer.firstname }} {{ customer.lastname }}
                                </span>
                            </div>
                            <div class="two-line">
                                <span>Email</span>
                                <span>{{ customer.email }}</span>
                            </div>
                            <div class="one-line segments">
                                <DTag
                                    :color="customer.status === 'active' ? 'success' : 'danger'"
                                    :label="capitalize(customer.status)"
                                />
                                <DTag
                                    v-if="dunningBlockUntilActive"
                                    color="danger"
                                    :label="__('customers.customers.dunning_stop_enabled')"
                                />
                                <DTag
                                    v-if="customer.in_dunning_process"
                                    color="danger"
                                    :label="__('customers.customers.in_dunning_process')"
                                />
                                <DTag
                                    v-for="segment in segments"
                                    :key="segment"
                                    :label="segment"
                                />
                                <IncidentFlag
                                    v-if="userPermissions.includes('customer.incidents_view') && customer.has_open_incidents"
                                    id="customer-tab-incident-flag"
                                    :active-incident="customer.has_open_incidents"
                                    :customer-id="customer.id"
                                />
                            </div>
                        </main>
                        <aside>
                            <div class="two-line">
                                <span>{{ __('customers.customers.active_mrr') }}</span>
                                <span>{{ priceFormatForDisplay(customer.active_mrr, 2, customer.market_region_id) }}</span>
                            </div>
                            <div class="two-line">
                                <span>{{ __('customers.customers.churned_mrr') }}</span>
                                <span>{{ priceFormatForDisplay(customer.churned_mrr, 2, customer.market_region_id) }}</span>
                            </div>
                            <div class="two-line">
                                <span>{{ __('customers.customers.total_mrr') }}</span>
                                <span>{{ priceFormatForDisplay(customer.total_mrr, 2, customer.market_region_id) }}</span>
                            </div>
                        </aside>
                    </div>
                </DCard>
                <DCard
                    v-if="showCreditBalancePanel"
                    padding
                >
                    <div class="credit-balance">
                        <main class="d-flex">
                            <div class="two-line me-3">
                                <span>{{ __('customers.customers.credit_balance') }}</span>
                                <span>{{ priceFormatForDisplay(creditBalance, 2, customer.market_region_id) }}</span>
                            </div>
                            <div
                                v-if="pendingRefund.amount > 0.0"
                                class="two-line"
                            >
                                <span>{{ __('customers.customers.requested_refund') }}</span>
                                <span>
                                    <span
                                        v-c-tooltip="pendingRefundDetailsTooltip"
                                        class="fw-normal"
                                    >
                                        {{ priceFormatForDisplay(pendingRefund.amount, 2, customer.market_region_id) }}
                                    </span>
                                </span>
                            </div>
                        </main>
                        <aside
                            v-if="refundSectionVisible && refundTooltip"
                            v-c-tooltip="refundTooltip"
                        >
                            <DButton
                                icon="close"
                                :hidden="refundCancelable === false"
                                :label="__('customers.customers.cancel_refund')"
                                @click="cancelRefund()"
                            />
                            <DButton
                                icon="cash-refund"
                                :hidden="refundVisible === false"
                                :label="__('customers.customers.credit_balance_refund')"
                                :disabled="refundDisabled"
                                @click="displayRefundModal()"
                            />
                        </aside>
                        <aside
                            v-else-if="refundSectionVisible"
                        >
                            <DButton
                                icon="close"
                                :hidden="refundCancelable === false"
                                :label="__('customers.customers.cancel_refund')"
                                @click="cancelRefund()"
                            />
                            <DButton
                                icon="cash-refund"
                                :hidden="refundVisible === false"
                                :label="__('customers.customers.credit_balance_refund')"
                                :disabled="refundDisabled"
                                @click="displayRefundModal()"
                            />
                        </aside>
                    </div>
                </DCard>
                <div class="tabs-wrapper">
                    <DTabs>
                        <DTabItem
                            v-if="userPermissions.includes('customer.view')"
                            :active="['customers.customers.show', 'customers.customers.edit'].includes($route().current())"
                            :label="__('customers.tabs.profile')"
                            :route-url="$route('customers.customers.show', {customer: customer.id})"
                        />
                        <DTabItem
                            v-if="userPermissions.includes('customer.view')"
                            :active="['customers.customers.contacts.index', 'customers.customers.contacts.create', 'customers.customers.contacts.show', 'customers.customers.contacts.edit'].includes($route().current())"
                            :label="__('customers.tabs.contacts')"
                            :route-url="$route('customers.customers.contacts.index', {customer: customer.id})"
                        />
                        <DTabItem
                            v-if="userPermissions.includes('contract.view')"
                            :active="['customers.customers.contracts.index', 'customers.customers.contracts.show', 'customers.customers.contracts.create', 'customers.customers.contracts.edit', 'customers.customers.contracts.phases.index', 'customers.customers.contracts.phases.edit', 'customers.customers.contracts.phases.create', 'customers.customers.contracts.partner_contracts', 'customers.customers.contracts.reseller_contracts', 'customers.customers.terminate_bankruptcy_contracts'].includes($route().current())"
                            :label="__('customers.tabs.contracts', { contractsCount: (typeof customer.contractsFilteredCount === 'number' ? customer.contractsFilteredCount +'/'+ customer.contractsCount : customer.contractsCount)})"
                            :route-url="$route('customers.customers.contracts.index', {customer: customer.id})"
                        />
                        <DTabItem
                            v-if="userPermissions.includes('invoice.view')"
                            :active="['customers.customers.invoices.index', 'customers.customers.invoices.create', 'customers.customers.invoices.show', 'customers.customers.credit-notes.create', 'customers.customers.credit-notes.show', 'customers.customers.invoices.edit', 'customers.customers.invoices.items', 'customers.customers.invoices.items-list', 'customers.customers.invoices.pending-invoice-items'].includes($route().current())"
                            :label="__('customers.tabs.invoices')"
                            :route-url="$route('customers.customers.invoices.index', {customer: customer.id})"
                        />
                        <DTabItem
                            v-if="userPermissions.includes('customer.view')"
                            :active="['customers.customers.communications.index', 'customers.customers.communications.messenger.index'].includes($route().current())"
                            :label="__('customers.tabs.communications')"
                            :route-url="$route('customers.customers.communications.index', {customer: customer.id})"
                        />
                        <DTabItem
                            v-if="userPermissions.includes('customer.view')"
                            :active="['customers.customers.activity-logs.index'].includes($route().current())"
                            :label="__('customers.tabs.activity_logs')"
                            :route-url="$route('customers.customers.activity-logs.index', {customer: customer.id})"
                        />
                        <DTabItem
                            v-if="userPermissions.includes('customer.view') && customer.is_reseller"
                            :active="['customers.customers.reseller-products.index', 'customers.customers.reseller-products.create', 'customers.customers.reseller-products.edit'].includes($route().current())"
                            :label="__('customers.tabs.reseller_products')"
                            :route-url="$route('customers.customers.reseller-products.index', {customer: customer.id})"
                        />
                        <DTabItem
                            v-if="userPermissions.includes('customer.view')"
                            :active="['customers.customers.transactions.index'].includes($route().current())"
                            :label="__('customers.tabs.transactions')"
                            :route-url="$route('customers.customers.transactions.index', {customer: customer.id})"
                        />
                        <DTabItem
                            v-if="userPermissions.includes('customer.view')"
                            :active="['customers.customers.orders.index'].includes($route().current())"
                            :label="__('customers.tabs.orders')"
                            :route-url="$route('customers.customers.orders.index', {customer: customer.id})"
                        />
                        <DTabItem
                            v-if="userPermissions.includes('project_sales') && $page.props.tenantSettings.enable_delivery_notes"
                            :active="['customers.customers.delivery_notes.index', 'customers.customers.delivery_notes.create'].includes($route().current())"
                            :label="__('customers.tabs.delivery_notes')"
                            :route-url="$route('customers.customers.delivery_notes.index', {customer: customer.id})"
                        />
                        <DTabItem
                            v-if="userPermissions.includes('customer.view')"
                            :active="['customers.customers.tickets.index'].includes($route().current())"
                            :label="__('customers.tabs.tickets')"
                            :route-url="$route('customers.customers.tickets.index', {customer: customer.id})"
                        />
                        <DTabItem
                            v-if="userPermissions.includes('customer.incidents_view')"
                            :active="['customers.customers.business-incidents.index', 'customers.customers.business-incidents.edit'].includes($route().current())"
                            :label="__('customers.tabs.incidents')"
                            :route-url="$route('customers.customers.business-incidents.index', {customer: customer.id})"
                        />
                        <DTabItem
                            v-if="userPermissions.includes('contract.edit.onehop.update_cluster_id')"
                            :active="['customers.customers.manage-clusters.index'].includes($route().current())"
                            :label="__('customers.tabs.manage_clusters')"
                            :route-url="$route('customers.customers.manage-clusters.index', {customer: customer.id})"
                        />
                    </DTabs>
                </div>
            </div>
        </div>
        <RefundModal
            v-if="refundSectionVisible && refundVisible && refundDisabled === false"
            :show-modal="showRefundModal"
            :customer="customer"
            :credit-balance="creditBalance"
            :refund-limit="refundLimit"
            @confirm="refundModalConfirm"
            @close-modal="closeRefundModal"
        />
        <slot />
    </div>
</template>

<script>
import RefundModal from '~@/Components/Customers/RefundModal.vue'
import priceFormat from '~@/Mixins/priceFormat'
import IncidentFlag from '~@/Components/Customers/BusinessIncidents/IncidentFlag'

export default {
    name: 'CustomerBase',
    components: { RefundModal, IncidentFlag },
    mixins: [priceFormat],
    props: {
        customer: {
            type: Object,
            default: () => { return {} }
        },
        creditBookingEnabled: {
            type: Boolean,
            default: () => { return false }
        },
        hasCreditBalance: {
            type: Boolean,
            default: () => { return false }
        },
        refundAllowedForUser: {
            type: Boolean,
            default: () => { return false }
        },
        refundLimit: {
            type: Number,
            default: () => { return 0.00 }
        },
        financeSystemConnectionWorking: {
            type: Boolean,
            default: () => { return false }
        },
        creditBalance: {
            type: Number,
            default: () => { return 0.00 }
        },
        pendingRefund: {
            type: Object,
            default: () => { return {} }
        },
        refundCancelable: {
            type: Boolean,
            default: () => { return false }
        }
    },
    data () {
        return {
            loginStatus: '',
            ssoLink: null,
            showRefundModal: false
        }
    },
    computed: {
        segments () {
            if (!this.customer || !this.customer.customer_segments || this.customer.customer_segments.length < 1) return []

            return this.customer.customer_segments
        },
        userPermissions () {
            return this.$page.props.auth.permissions
        },
        refundSectionVisible () {
            const currentRoute = this.$route().current()

            return ['customers.customers.show', 'customers.customers.edit'].includes(currentRoute) === false
        },
        refundVisible () {
            return this.creditBookingEnabled && this.hasCreditBalance && this.refundAllowedForUser
        },
        refundDisabled () {
            let hasManualPendingRefund = false

            for (const refund of this.pendingRefund.refunds) {
                if (!refund.invoice_id) {
                    hasManualPendingRefund = true
                }
            }

            return this.financeSystemConnectionWorking === false || hasManualPendingRefund === true
        },
        pendingRefundDetailsTooltip () {
            return {
                placement: 'right',
                content: this.pendingRefund
                    .refunds
                    .map((refund) => {
                        const refundText = refund.invoice_id
                            ? this.__('customers.customers.requested_refund_invoice', { invoice_id: refund.invoice_id })
                            : this.__('customers.customers.requested_refund_refund')

                        return `<b>${refundText}:</b> ${this.priceFormatForDisplay(refund.amount, 2, this.customer.market_region_id)}`
                    })
                    .join('<br>')
            }
        },
        refundTooltip () {
            if (this.financeSystemConnectionWorking === false) {
                return this.__('customers.customers.finance_system_connection_not_working')
            }

            return false
        },
        dunningBlockUntilActive () {
            if (this.customer.dunning_block_until === null) {
                return false
            }

            const dunningBlockUntilParts = this.customer.dunning_block_until.split('-')
            const dunningBlockUntil = new Date(dunningBlockUntilParts[0], dunningBlockUntilParts[1] - 1, dunningBlockUntilParts[2])

            const now = new Date()
            now.setHours(0, 0, 0, 0)

            return dunningBlockUntil >= now
        },
        showCreditBalancePanel () {
            return this.userPermissions.includes('invoice.view') &&
                [
                    'customers.customers.invoices.index',
                    'customers.customers.invoices.create',
                    'customers.customers.invoices.show',
                    'customers.customers.credit-notes.create',
                    'customers.customers.credit-notes.show',
                    'customers.customers.invoices.edit',
                    'customers.customers.invoices.items',
                    'customers.customers.show',
                    'customers.customers.edit',
                    'customers.customers.invoices.pending-invoice-items'
                ].includes(this.$route().current())
        }
    },
    methods: {
        capitalize (text) {
            return text.charAt(0).toUpperCase() + text.slice(1)
        },
        cancelRefund () {
            this.showRefundModal = false

            this.$inertia.post(this.$route('customers.refund.cancel_refund', {
                customer: this.customer.id
            }), {
                onError: () => {
                    this.showRefundModal = true
                }
            })
        },
        displayRefundModal () {
            this.showRefundModal = true
        },
        refundModalConfirm (form) {
            form.post(this.$route('customers.refund.issue_a_refund', {
                customer: this.customer.id
            }))
        },
        closeRefundModal () {
            this.showRefundModal = false
        }
    }
}
</script>

<style lang="scss" scoped>
.customer-profile {
    display: flex;
    gap: 15px;
    flex-direction: column;

    & .overview {
        display: grid;
        grid-template-columns: 1fr auto;
        grid-gap: 20px;
        padding: 5px;

        & main {
            display: flex;
            grid-gap: 20px;
        }
        & aside {
            display: flex;
            grid-gap: 20px;
        }

        & .two-line {
            display: grid;
            grid-gap: 5px;

            & span:nth-child(1) {
                font-weight: bold;
            }
        }
        & .two-line-second-bold {
            display: grid;
            grid-gap: 5px;

            & span:nth-child(2) {
                font-weight: bold;
            }
        }
        & .one-line {
            display: flex;
            align-items: center;

            &.segments {
                grid-gap: 5px;
            }
        }
    }
    & .credit-balance {
        display: grid;
        grid-gap: 15px;
        grid-template-columns: 1fr auto;

        & .two-line {
            display: grid;
            grid-gap: 5px;

            & span:nth-child(1) {
                font-weight: bold;
            }
        }
    }
    & .sticky-top {
        top: -30px;
        & .tabs-wrapper {
            background: var(--colorAppBase);
            border-radius: 5px;
            border: 1px solid var(--colorAppBorder);
        }
    }
}
</style>
